import { useState, useCallback } from 'react';
import { NotificationType } from '../components/ui/overlays/modals/NotificationModal';

interface NotificationState {
  isOpen: boolean;
  type: NotificationType;
  title?: string;
  message: string;
  duration?: number;
}

interface ShowNotificationProps {
  type: NotificationType;
  message: string;
  title?: string;
  duration?: number;
}

export const useNotification = () => {
  const [notification, setNotification] = useState<NotificationState>({
    isOpen: false,
    type: 'info',
    message: ''
  });

  const showNotification = useCallback(({ type, message, title, duration }: ShowNotificationProps) => {
    setNotification({
      isOpen: true,
      type,
      message,
      title,
      duration
    });
  }, []);

  const hideNotification = useCallback(() => {
    setNotification(prev => ({ ...prev, isOpen: false }));
  }, []);

  // Convenience methods
  const showSuccess = useCallback((message: string, title?: string) => {
    showNotification({ type: 'success', message, title });
  }, [showNotification]);

  const showError = useCallback((message: string, title?: string) => {
    showNotification({ type: 'error', message, title });
  }, [showNotification]);

  const showWarning = useCallback((message: string, title?: string) => {
    showNotification({ type: 'warning', message, title });
  }, [showNotification]);

  const showInfo = useCallback((message: string, title?: string) => {
    showNotification({ type: 'info', message, title });
  }, [showNotification]);

  return {
    notification,
    showNotification,
    hideNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo
  };
};